import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Star, Trash2 } from "lucide-react";
import { api, fileUrl, formatApiError } from "../../lib/api";

export const PhotosManager = ({ profile, onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/profile/me/photos", fd);
        onChange(data);
      }
      toast.success("Photos uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const act = async (fn, msg) => {
    try {
      const { data } = await fn();
      onChange(data);
      toast.success(msg);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const full = profile.photos.length >= 8;

  return (
    <div data-testid="photos-manager">
      <p className="text-white/60 text-sm max-w-xl">Upload up to 8 photos (JPG, PNG or WEBP, under 8MB). The first photo is your main headshot shown in search.</p>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={upload} data-testid="photos-file-input" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        {profile.photos.map((p, i) => (
          <div key={p.id} className="relative group aspect-[4/5] bg-muted overflow-hidden border border-white/10" data-testid={`photo-item-${i}`}>
            <img src={fileUrl(p.path)} alt="" className="w-full h-full object-cover" />
            {i === 0 && <span className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest px-2 py-1">Main</span>}
            <div className="absolute inset-x-0 bottom-0 flex justify-end gap-2 p-2 bg-black/70 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
              {i !== 0 && (
                <button onClick={() => act(() => api.post(`/profile/me/photos/${p.id}/primary`), "Main photo updated")} className="p-2 hover:text-primary" title="Make main photo" data-testid={`photo-primary-${i}`}><Star size={16} /></button>
              )}
              <button onClick={() => act(() => api.delete(`/profile/me/photos/${p.id}`), "Photo removed")} className="p-2 hover:text-red-400" title="Delete" data-testid={`photo-delete-${i}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {!full && (
          <button onClick={() => inputRef.current?.click()} disabled={uploading} className="aspect-[4/5] border-2 border-dashed border-white/15 hover:border-primary hover:text-primary flex flex-col items-center justify-center gap-3 text-white/50 transition-colors disabled:opacity-60" data-testid="photos-upload-button">
            <ImagePlus size={32} />
            <span className="text-xs uppercase tracking-widest font-bold">{uploading ? "Uploading…" : "Add photos"}</span>
          </button>
        )}
      </div>
    </div>
  );
};
